import React, { useState, useMemo } from 'react';
import { TransactionComp, AccessTier } from '../../types';
import { maxCompsAllowed } from '../../config/accessTiers';
import {
  Search,
  ArrowUpDown,
  Download,
  ExternalLink,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Lock,
  Layers,
  FileText,
  Building,
  MapPin,
  Zap,
} from 'lucide-react';
import { getCountyRecorderUrl, getSecEdgarUrl, getIsoQueuePortalUrl } from '../../lib/sourceLinks';

interface TransactionsLedgerProps {
  comps: TransactionComp[];
  selectedCompId: string | null;
  onSelectComp: (comp: TransactionComp) => void;
  tier: AccessTier;
  search: string;
  onSearchChange: (val: string) => void;
  selectedState: string;
  onStateChange: (val: string) => void;
  selectedDealType: string;
  onDealTypeChange: (val: string) => void;
  selectedMinMw: string;
  onMinMwChange: (val: string) => void;
  onOpenAuthModal: () => void;
}

type SortField =
  | 'canonical_name'
  | 'closing_date'
  | 'total_consideration_usd'
  | 'headline_capacity_mw'
  | 'implied_price_per_acre_usd'
  | 'implied_price_per_mw_usd';

export const TransactionsLedger: React.FC<TransactionsLedgerProps> = ({
  comps,
  selectedCompId,
  onSelectComp,
  tier,
  search,
  onSearchChange,
  selectedState,
  onStateChange,
  selectedDealType,
  onDealTypeChange,
  selectedMinMw,
  onMinMwChange,
  onOpenAuthModal,
}) => {
  const maxAllowed = maxCompsAllowed(tier);
  const isGated = comps.length > maxAllowed;

  // Sorting
  const [sortField, setSortField] = useState<SortField>('closing_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Collect unique states from comps
  const availableStates = useMemo(() => {
    const set = new Set<string>();
    comps.forEach((c) => {
      if (c.state) set.add(c.state.trim().toUpperCase());
    });
    return Array.from(set).sort();
  }, [comps]);

  // Sort and filter comps
  const sortedComps = useMemo(() => {
    const list = [...comps];
    list.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      if (typeof valA === 'string') {
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    });
    return list;
  }, [comps, sortField, sortOrder]);

  const visibleComps = useMemo(() => {
    return sortedComps.slice(0, maxAllowed);
  }, [sortedComps, maxAllowed]);

  const totalPages = Math.max(1, Math.ceil(visibleComps.length / pageSize));
  const paginatedComps = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleComps.slice(start, start + pageSize);
  }, [visibleComps, page, pageSize]);

  // Export CSV
  const handleExportCsv = () => {
    const headers = [
      'Canonical_Project_ID',
      'Asset_Name',
      'Deal_Name',
      'Deal_Type',
      'Closing_Date',
      'State',
      'County',
      'Capacity_MW',
      'Acreage',
      'Total_Consideration_USD',
      'Price_Per_Acre_USD',
      'Price_Per_MW_USD',
      'Buyer',
      'Seller',
    ];

    const rows = visibleComps.map((c) => [
      `"${c.canonical_project_id}"`,
      `"${c.canonical_name || ''}"`,
      `"${c.deal_name}"`,
      `"${c.deal_type}"`,
      `"${c.closing_date}"`,
      `"${c.state || ''}"`,
      `"${c.county || ''}"`,
      c.headline_capacity_mw ?? '',
      c.headline_acreage ?? '',
      c.total_consideration_usd ?? '',
      c.implied_price_per_acre_usd ?? '',
      c.implied_price_per_mw_usd ?? '',
      `"${c.buyer_organization_id || ''}"`,
      `"${c.seller_organization_id || ''}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `aixenergy_deal_studio_comps_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) {
      return <span className="text-slate-500 font-mono text-[11px] italic">Non-Disclosure (TX)</span>;
    }
    if (price >= 1_000_000_000) {
      return <span className="text-white font-mono font-bold">${(price / 1_000_000_000).toFixed(2)}B</span>;
    }
    return <span className="text-white font-mono font-bold">${(price / 1_000_000).toFixed(1)}M</span>;
  };

  const getDealTypeBadge = (dealType: string) => {
    switch (dealType) {
      case 'POWERED_LAND_SALE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 whitespace-nowrap">
            POWERED LAND
          </span>
        );
      case 'EQUITY_JV':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/30 whitespace-nowrap">
            EQUITY JV
          </span>
        );
      case 'BTS_LEASE':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 whitespace-nowrap">
            BTS LEASE
          </span>
        );
      case 'PLATFORM_MA':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">
            PLATFORM M&amp;A
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono text-slate-400 border border-slate-700 whitespace-nowrap">
            {dealType.replace(/_/g, ' ')}
          </span>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#06090e] overflow-hidden select-none">
      {/* 1. Control & Filter Bar */}
      <div className="p-3 bg-[#0c121e] border-b border-[#1a253b] flex flex-wrap items-center justify-between gap-3">
        {/* Search */}
        <div className="flex items-center space-x-2 flex-1 min-w-[280px] max-w-md">
          <div className="relative w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by deal, campus, county, buyer, seller..."
              value={search}
              onChange={(e) => {
                onSearchChange(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 outline-none hover:border-slate-700 focus:border-[#2979ff] transition"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 text-xs flex-wrap gap-y-2">
          {/* State Dropdown */}
          <select
            value={selectedState}
            onChange={(e) => {
              onStateChange(e.target.value);
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value="">All States ({availableStates.length})</option>
            {availableStates.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>

          {/* Deal Type Dropdown */}
          <select
            value={selectedDealType}
            onChange={(e) => {
              onDealTypeChange(e.target.value);
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value="">All Deal Types</option>
            <option value="POWERED_LAND_SALE">Powered Land Sale</option>
            <option value="EQUITY_JV">Equity Joint Venture</option>
            <option value="BTS_LEASE">Build-to-Suit Lease</option>
            <option value="PLATFORM_MA">Platform M&amp;A</option>
          </select>

          {/* Minimum Capacity Dropdown */}
          <select
            value={selectedMinMw}
            onChange={(e) => {
              onMinMwChange(e.target.value);
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value="">All Capacities</option>
            <option value="100">100+ MW</option>
            <option value="250">250+ MW</option>
            <option value="500">500+ MW</option>
            <option value="1000">1,000+ MW (Gigawatt)</option>
          </select>

          {/* Export CSV Button */}
          <button
            onClick={handleExportCsv}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] text-slate-300 hover:text-white font-mono text-xs transition"
            title="Export filtered records to institutional CSV"
          >
            <Download className="w-3.5 h-3.5 text-[#2979ff]" />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Counter & Gating Alert */}
        <div className="flex items-center space-x-3 text-xs">
          <div className="text-slate-400 font-mono text-[11px]">
            Showing <b className="text-white">{visibleComps.length}</b> of <b className="text-white">{comps.length}</b> verified transactions
          </div>
          {isGated && (
            <button
              onClick={onOpenAuthModal}
              className="flex items-center space-x-1 text-amber-400 hover:text-amber-300 font-mono text-xs underline decoration-amber-500/50"
            >
              <Lock className="w-3 h-3" />
              <span>Unlock All {comps.length} Comps</span>
            </button>
          )}
        </div>
      </div>

      {/* 2. Full-Width Tabular Ledger */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-[#1a253b] bg-[#0c121e] text-slate-400 font-mono text-[11px] uppercase tracking-wider">
              <th
                onClick={() => handleSort('canonical_name')}
                className="py-3 px-4 font-semibold cursor-pointer hover:text-white transition"
              >
                <div className="flex items-center space-x-1.5">
                  <span>Canonical Asset / Campus</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-500" />
                </div>
              </th>

              <th className="py-3 px-4 font-semibold">Deal Name &amp; Structure</th>

              <th className="py-3 px-4 font-semibold">Transaction Parties</th>

              <th
                onClick={() => handleSort('headline_capacity_mw')}
                className="py-3 px-4 font-semibold text-right cursor-pointer hover:text-white transition"
              >
                <div className="flex items-center justify-end space-x-1.5">
                  <span>Capacity / Land</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-500" />
                </div>
              </th>

              <th
                onClick={() => handleSort('total_consideration_usd')}
                className="py-3 px-4 font-semibold text-right cursor-pointer hover:text-white transition"
              >
                <div className="flex items-center justify-end space-x-1.5">
                  <span>Total Consideration</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-500" />
                </div>
              </th>

              <th
                onClick={() => handleSort('implied_price_per_mw_usd')}
                className="py-3 px-4 font-semibold text-right cursor-pointer hover:text-white transition"
              >
                <div className="flex items-center justify-end space-x-1.5">
                  <span>Valuation Multiples</span>
                  <ArrowUpDown className="w-3 h-3 text-slate-500" />
                </div>
              </th>

              <th className="py-3 px-4 font-semibold text-center">Verified Source Evidence</th>

              <th className="py-3 px-4 font-semibold text-right">Diligence</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#1a253b]/60">
            {paginatedComps.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-slate-500 font-mono">
                  No verified transactions match your search filters.
                </td>
              </tr>
            ) : (
              paginatedComps.map((comp) => {
                const isSelected = selectedCompId === comp.canonical_project_id;
                const countyLink = getCountyRecorderUrl(comp.county, comp.state);
                const isoLink = comp.iso_rto ? getIsoQueuePortalUrl(comp.iso_rto) : null;

                return (
                  <tr
                    key={comp.deal_id || comp.canonical_project_id}
                    onClick={() => onSelectComp(comp)}
                    className={`cursor-pointer transition-colors group ${
                      isSelected ? 'bg-[#121a2d] border-l-2 border-l-[#2979ff]' : 'hover:bg-[#0c121e]/80'
                    }`}
                  >
                    {/* 1. Canonical Asset & Location */}
                    <td className="py-3 px-4">
                      <div className="space-y-0.5">
                        <div className="flex items-center space-x-1.5">
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#2979ff]/10 text-[#2979ff] border border-[#2979ff]/30 font-bold">
                            {comp.canonical_project_id}
                          </span>
                          <span className="text-[10px] font-mono font-semibold text-slate-400">
                            {comp.state}
                          </span>
                          {comp.iso_rto && (
                            <span className="text-[10px] font-mono px-1 rounded bg-[#162035] text-slate-400">
                              {comp.iso_rto}
                            </span>
                          )}
                        </div>
                        <div className="font-semibold text-white group-hover:text-[#2979ff] transition-colors leading-tight">
                          {comp.canonical_name || comp.deal_name}
                        </div>
                        <div className="text-[11px] text-slate-400 flex items-center space-x-1">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span>{comp.county ? `${comp.county} County, ` : ''}{comp.state}</span>
                        </div>
                      </div>
                    </td>

                    {/* 2. Deal Name & Structure */}
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-1.5">
                          {getDealTypeBadge(comp.deal_type)}
                          <span className="text-[11px] font-mono text-slate-400">
                            {comp.closing_date}
                          </span>
                        </div>
                        <div className="text-xs text-slate-300 font-medium line-clamp-1" title={comp.deal_name}>
                          {comp.deal_name}
                        </div>
                      </div>
                    </td>

                    {/* 3. Entities */}
                    <td className="py-3 px-4 font-mono text-xs">
                      <div className="space-y-0.5">
                        <div className="text-slate-300 truncate max-w-[200px]" title={comp.buyer_organization_id || 'Institutional Sponsor'}>
                          <span className="text-slate-500 text-[10px]">Buyer: </span>
                          <span className="text-white font-medium">
                            {(comp.buyer_organization_id || 'Confidential Sponsor').replace('AOI-', '')}
                          </span>
                        </div>
                        <div className="text-slate-400 truncate max-w-[200px] text-[11px]" title={comp.seller_organization_id || 'Landowner / SPV'}>
                          <span className="text-slate-500 text-[10px]">Seller: </span>
                          <span>
                            {(comp.seller_organization_id || 'Private Landowner SPV').replace('AOI-', '')}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* 4. Capacity & Land */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className="space-y-0.5">
                        <div className="font-bold text-white text-xs">
                          {comp.headline_capacity_mw ? `${comp.headline_capacity_mw.toLocaleString()} MW` : 'N/A'}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {comp.headline_acreage ? `${comp.headline_acreage.toLocaleString()} acres` : 'Acreage unstated'}
                        </div>
                      </div>
                    </td>

                    {/* 5. Total Consideration */}
                    <td className="py-3 px-4 text-right">
                      {formatPrice(comp.total_consideration_usd)}
                    </td>

                    {/* 6. Multiples */}
                    <td className="py-3 px-4 text-right font-mono text-xs">
                      <div className="space-y-0.5">
                        {comp.implied_price_per_mw_usd ? (
                          <div className="text-cyan-400 font-semibold">
                            ${(comp.implied_price_per_mw_usd / 1_000).toFixed(0)}k/MW
                          </div>
                        ) : (
                          <div className="text-slate-500 text-[11px]">—</div>
                        )}
                        {comp.implied_price_per_acre_usd ? (
                          <div className="text-emerald-400 text-[11px]">
                            ${(comp.implied_price_per_acre_usd / 1_000).toFixed(0)}k/ac
                          </div>
                        ) : (
                          <div className="text-slate-500 text-[11px]">—</div>
                        )}
                      </div>
                    </td>

                    {/* 7. Verified Sources with DIRECT Links */}
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-1.5">
                        {/* County Clerk Link */}
                        <a
                          href={countyLink.url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1 rounded bg-[#162035] hover:bg-[#1f2d47] border border-[#1a253b] text-amber-400 transition"
                          title={`Inspect ${countyLink.label}`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </a>

                        {/* SEC Link */}
                        <a
                          href={getSecEdgarUrl(null, comp.buyer_organization_id).url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1 rounded bg-[#162035] hover:bg-[#1f2d47] border border-[#1a253b] text-cyan-400 transition"
                          title="Inspect SEC EDGAR disclosures"
                        >
                          <Building className="w-3.5 h-3.5" />
                        </a>

                        {/* ISO Link */}
                        {isoLink && (
                          <a
                            href={isoLink.url}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1 rounded bg-[#162035] hover:bg-[#1f2d47] border border-[#1a253b] text-emerald-400 transition"
                            title={`Inspect ${isoLink.label}`}
                          >
                            <Zap className="w-3.5 h-3.5" />
                          </a>
                        )}

                        {/* SHA-256 Provenance */}
                        <span
                          className="p-1 rounded bg-[#162035] border border-[#1a253b] text-blue-400"
                          title="Cryptographically verified SHA-256 evidence record"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </td>

                    {/* 8. Action button */}
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectComp(comp);
                        }}
                        className="px-2.5 py-1 rounded bg-[#2979ff]/15 hover:bg-[#2979ff]/25 border border-[#2979ff]/30 text-[#2979ff] hover:text-white font-mono text-[11px] font-semibold transition"
                      >
                        Inspect Dossier &rarr;
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 3. Pagination Footer */}
      <div className="p-3 bg-[#0c121e] border-t border-[#1a253b] flex items-center justify-between text-xs text-slate-400 font-mono">
        <div className="flex items-center space-x-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded px-2 py-1 outline-none text-xs"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-slate-500">| Page {page} of {totalPages}</span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
            title="Previous Page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
            title="Next Page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
